import intl from "react-intl-universal";
import { formatFilterDate, startOfDay, endOfDay } from "../../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  BAAS_ENTITY_KYB_STATUSES,
  BAAS_ENTITY_STATUSES,
  BAAS_ENTITY_TYPES,
} from "../../../../Services/baasEntidadesApi";

export const EMPTY_BAAS_ENTIDADES_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  statuses: [],
  types: [],
  kybStatuses: [],
};

export const getBaasEntityStatusOptions = () =>
  BAAS_ENTITY_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.entidades.status.${status}`),
  }));

export const getBaasEntityTypeOptions = () =>
  BAAS_ENTITY_TYPES.map((type) => ({
    id: type,
    label: intl.get(`baas.entidades.type.${type}`),
  }));

export const getBaasEntityKybStatusOptions = () =>
  BAAS_ENTITY_KYB_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.entidades.kybStatus.${status}`),
  }));

const matchesSearch = (entity, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [entity.name, entity.document, entity.id]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (entity, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const createdAt = new Date(entity.createdAt);
  if (Number.isNaN(createdAt.getTime())) return false;
  if (dateFrom && createdAt < startOfDay(dateFrom)) return false;
  if (dateTo && createdAt > endOfDay(dateTo)) return false;
  return true;
};

export const filterBaasEntidades = (items = [], filters = EMPTY_BAAS_ENTIDADES_FILTERS) =>
  items.filter((entity) => {
    if (!matchesSearch(entity, filters.searchQuery)) return false;
    if (!matchesDateRange(entity, filters.dateFrom, filters.dateTo)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(entity.status)) return false;
    if (filters.types?.length && !filters.types.includes(entity.type)) return false;
    if (filters.kybStatuses?.length && !filters.kybStatuses.includes(entity.kybStatus)) return false;
    return true;
  });

export const hasActiveBaasEntidadesFilters = (filters = EMPTY_BAAS_ENTIDADES_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.statuses?.length ||
      filters.types?.length ||
      filters.kybStatuses?.length,
  );

export const isBaasEntidadesChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "status":
      return Boolean(filters.statuses?.length);
    case "type":
      return Boolean(filters.types?.length);
    case "kyb":
      return Boolean(filters.kybStatuses?.length);
    case "more":
      return Boolean(filters.kybStatuses?.length);
    default:
      return false;
  }
};

export const buildBaasEntidadesFilterTags = (filters = EMPTY_BAAS_ENTIDADES_FILTERS) => {
  const tags = [];
  const statusOptions = getBaasEntityStatusOptions();
  const typeOptions = getBaasEntityTypeOptions();
  const kybOptions = getBaasEntityKybStatusOptions();

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromLabel = filters.dateFrom ? formatFilterDate(filters.dateFrom) : "…";
    const toLabel = filters.dateTo ? formatFilterDate(filters.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((statusId) => statusOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  if (filters.types?.length) {
    tags.push({
      key: "types",
      iconType: "type",
      value: filters.types
        .map((typeId) => typeOptions.find((item) => item.id === typeId)?.label ?? typeId)
        .join(", "),
    });
  }

  if (filters.kybStatuses?.length) {
    tags.push({
      key: "kybStatuses",
      iconType: "subAccount",
      value: filters.kybStatuses
        .map((statusId) => kybOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  return tags;
};

export const removeBaasEntidadesFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "statuses":
      return { ...filters, statuses: [] };
    case "types":
      return { ...filters, types: [] };
    case "kybStatuses":
      return { ...filters, kybStatuses: [] };
    default:
      return filters;
  }
};
